import axios from 'axios';
import { storage } from '@byscott-io/corebyscott';

const client = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

client.interceptors.request.use((config) => {
  const token = storage.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const accountId = storage.getContainerId();
  if (accountId) {
    config.headers['X-Account-Id'] = accountId;
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      storage.clear();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const dashboard = {
  get: () => client.get('/dashboard'),
  runBillingCycle: (appId = null) => client.post('/dashboard/run_billing_cycle', { app_id: appId }),
  processExpiredTrials: (appId = null) => client.post('/dashboard/process_expired_trials', { app_id: appId }),
};

export const account = {
  get: () => client.get('/account'),
  update: (data) => client.patch('/account', { account: data }),
  getSquareConfig: () => client.get('/account/square_config'),
  updateSquareConfig: (config) => client.patch('/account/square_config', config),
};

export const apps = {
  list: () => client.get('/apps'),
  get: (id) => client.get(`/apps/${id}`),
  create: (app) => client.post('/apps', { app }),
  update: (id, app) => client.patch(`/apps/${id}`, { app }),
  delete: (id) => client.delete(`/apps/${id}`),
  regenerateKey: (id) => client.post(`/apps/${id}/regenerate_key`),
  clientAccounts: (appId) => client.get(`/apps/${appId}/client_accounts`),
  clientAccount: (appId, id) => client.get(`/apps/${appId}/client_accounts/${id}`),
};

export const products = {
  list: (appId) => client.get(`/apps/${appId}/products`),
  get: (appId, id) => client.get(`/apps/${appId}/products/${id}`),
  create: (appId, product) => client.post(`/apps/${appId}/products`, { product }),
  update: (appId, id, product) => client.patch(`/apps/${appId}/products/${id}`, { product }),
  delete: (appId, id) => client.delete(`/apps/${appId}/products/${id}`),
};

export const productLevels = {
  list: (appId, productId) => client.get(`/apps/${appId}/products/${productId}/levels`),
  get: (appId, productId, id) => client.get(`/apps/${appId}/products/${productId}/levels/${id}`),
  create: (appId, productId, level) => client.post(`/apps/${appId}/products/${productId}/levels`, { level }),
  update: (appId, productId, id, level) => client.patch(`/apps/${appId}/products/${productId}/levels/${id}`, { level }),
  delete: (appId, productId, id) => client.delete(`/apps/${appId}/products/${productId}/levels/${id}`),
  addFeature: (appId, productId, id, featureId, enabled) =>
    client.post(`/apps/${appId}/products/${productId}/levels/${id}/add_feature`, { feature_id: featureId, enabled }),
  removeFeature: (appId, productId, id, featureId) =>
    client.delete(`/apps/${appId}/products/${productId}/levels/${id}/features/${featureId}`),
  addQuotum: (appId, productId, id, quotumId, value) =>
    client.post(`/apps/${appId}/products/${productId}/levels/${id}/add_quotum`, { quotum_id: quotumId, value }),
  removeQuotum: (appId, productId, id, quotumId) =>
    client.delete(`/apps/${appId}/products/${productId}/levels/${id}/quota/${quotumId}`),
  addSetting: (appId, productId, id, settingId, value) =>
    client.post(`/apps/${appId}/products/${productId}/levels/${id}/add_setting`, { setting_id: settingId, value }),
  removeSetting: (appId, productId, id, settingId) =>
    client.delete(`/apps/${appId}/products/${productId}/levels/${id}/settings/${settingId}`),
  // Pricing
  getPrices: (appId, productId, id) =>
    client.get(`/apps/${appId}/products/${productId}/levels/${id}/prices`),
  setPrice: (appId, productId, id, priceData) =>
    client.post(`/apps/${appId}/products/${productId}/levels/${id}/set_price`, priceData),
  removePrice: (appId, productId, id, billingInterval) =>
    client.delete(`/apps/${appId}/products/${productId}/levels/${id}/prices/${billingInterval}`),
};

export const features = {
  list: (appId) => client.get(`/apps/${appId}/features`),
  get: (appId, id) => client.get(`/apps/${appId}/features/${id}`),
  create: (appId, feature) => client.post(`/apps/${appId}/features`, { feature }),
  update: (appId, id, feature) => client.patch(`/apps/${appId}/features/${id}`, { feature }),
  delete: (appId, id) => client.delete(`/apps/${appId}/features/${id}`),
};

export const quota = {
  list: (appId) => client.get(`/apps/${appId}/quota`),
  get: (appId, id) => client.get(`/apps/${appId}/quota/${id}`),
  create: (appId, quotum) => client.post(`/apps/${appId}/quota`, { quotum }),
  update: (appId, id, quotum) => client.patch(`/apps/${appId}/quota/${id}`, { quotum }),
  delete: (appId, id) => client.delete(`/apps/${appId}/quota/${id}`),
};

export const settings = {
  list: (appId) => client.get(`/apps/${appId}/settings`),
  get: (appId, id) => client.get(`/apps/${appId}/settings/${id}`),
  create: (appId, setting) => client.post(`/apps/${appId}/settings`, { setting }),
  update: (appId, id, setting) => client.patch(`/apps/${appId}/settings/${id}`, { setting }),
  delete: (appId, id) => client.delete(`/apps/${appId}/settings/${id}`),
};

export const coupons = {
  list: (appId) => client.get(`/apps/${appId}/coupons`),
  get: (appId, id) => client.get(`/apps/${appId}/coupons/${id}`),
  create: (appId, coupon) => client.post(`/apps/${appId}/coupons`, { coupon }),
  update: (appId, id, coupon) => client.patch(`/apps/${appId}/coupons/${id}`, { coupon }),
  delete: (appId, id) => client.delete(`/apps/${appId}/coupons/${id}`),
};

export const affiliates = {
  list: (activeOnly = false) => client.get('/affiliates', { params: { active_only: activeOnly } }),
  get: (id) => client.get(`/affiliates/${id}`),
  create: (affiliate) => client.post('/affiliates', { affiliate }),
  update: (id, affiliate) => client.patch(`/affiliates/${id}`, { affiliate }),
  delete: (id) => client.delete(`/affiliates/${id}`),
};

export const referralCodes = {
  list: (appId, filters = {}) => client.get(`/apps/${appId}/referral_codes`, { params: filters }),
  get: (appId, id) => client.get(`/apps/${appId}/referral_codes/${id}`),
  create: (appId, referral_code) => client.post(`/apps/${appId}/referral_codes`, { referral_code }),
  update: (appId, id, referral_code) => client.patch(`/apps/${appId}/referral_codes/${id}`, { referral_code }),
  delete: (appId, id) => client.delete(`/apps/${appId}/referral_codes/${id}`),
};

export const subscriptions = {
  listForApp: (appId) => client.get(`/apps/${appId}/subscriptions`),
  listForClient: (appId, clientId) => client.get(`/apps/${appId}/client_accounts/${clientId}/subscriptions`),
  get: (appId, clientId, id) => client.get(`/apps/${appId}/client_accounts/${clientId}/subscriptions/${id}`),
  create: (appId, clientId, data) =>
    client.post(`/apps/${appId}/client_accounts/${clientId}/subscriptions`, data),
  update: (appId, clientId, id, subscription) =>
    client.patch(`/apps/${appId}/client_accounts/${clientId}/subscriptions/${id}`, { subscription }),
  cancel: (appId, clientId, id, immediate = false) =>
    client.post(`/apps/${appId}/client_accounts/${clientId}/subscriptions/${id}/cancel`, { immediate }),
  reactivate: (appId, clientId, id) =>
    client.post(`/apps/${appId}/client_accounts/${clientId}/subscriptions/${id}/reactivate`),
  addOverride: (appId, clientId, id, override) =>
    client.post(`/apps/${appId}/client_accounts/${clientId}/subscriptions/${id}/add_override`, override),
  removeOverride: (appId, clientId, id, override) =>
    client.delete(`/apps/${appId}/client_accounts/${clientId}/subscriptions/${id}/remove_override`, { data: override }),
  generateInvoice: (appId, clientId, subscriptionId, data = {}) =>
    client.post(`/apps/${appId}/client_accounts/${clientId}/subscriptions/${subscriptionId}/generate_invoice`, data),
  // Trial management
  extendTrial: (appId, clientId, id, days) =>
    client.post(`/apps/${appId}/client_accounts/${clientId}/subscriptions/${id}/extend_trial`, { days }),
  convertTrial: (appId, clientId, id, chargeImmediately = true) =>
    client.post(`/apps/${appId}/client_accounts/${clientId}/subscriptions/${id}/convert_trial`, { charge_immediately: chargeImmediately }),
};

export const invoices = {
  listForApp: (appId, params = {}) => client.get(`/apps/${appId}/invoices`, { params }),
  listForClient: (appId, clientId) => client.get(`/apps/${appId}/client_accounts/${clientId}/invoices`),
  get: (appId, clientId, id) => client.get(`/apps/${appId}/client_accounts/${clientId}/invoices/${id}`),
  void: (appId, clientId, id) =>
    client.post(`/apps/${appId}/client_accounts/${clientId}/invoices/${id}/void`),
  charge: (appId, clientId, id) =>
    client.post(`/apps/${appId}/client_accounts/${clientId}/invoices/${id}/charge`),
};

export default client;
